import { Perk } from '@models/membership/Perk'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '@store/store'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { perkSchema } from '../../models/schema'
import {
  createPerk as createPerkAPI,
  deletePerk as deletePerkAPI,
  fetchPerkById,
  fetchPerks,
  updatePerk as updatePerkAPI,
} from '../../services/membership/PerkService'

const initialState: SBState<Perk> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'perk',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getPerksSuccess,
  setQuery: setPerkQuery,
  reset: resetPerkState,
  resetQueryAndIds: resetPerkQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: Perk }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectPerks = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedPerk = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectPerkById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectPerksByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedPerk =
  (perk: Perk | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<Perk>(perk)))

export const getPerks = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchPerks(params),
    [perkSchema],
    slice.actions
  )

export const createPerk = (perk: FormData) =>
  SBAPI.CreateDispatched(createPerkAPI(perk), perkSchema, slice.actions)

export const getPerkWithId = (id: number) =>
  SBAPI.FetchDispatched<Perk>(fetchPerkById(id), perkSchema, slice.actions)

export const updatePerk = (perk: FormData) =>
  SBAPI.UpdateDispatched<Perk>(updatePerkAPI(perk), perk, slice.actions)

export const deletePerk = (perk: Perk) =>
  SBAPI.DestroyDispatched<Perk>(deletePerkAPI(perk), perk, slice.actions)
